import { useCallback, useEffect, useRef, useState } from "react";
import useStatusParam from "../../hooks/useStatusParam";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import ActiveFilters, { FilterChip } from "../../app-components/ui/ActiveFilters";
import ColumnMenu, { ColumnDef, useColumnVisibility } from "../../app-components/ui/ColumnMenu";
import DateFilter from "../../app-components/ui/DateFilter";
import EmptyState from "../../app-components/ui/EmptyState";
import PageHeader from "../../app-components/ui/PageHeader";
import Pagination, { PER_PAGE_OPTIONS } from "../../app-components/ui/Pagination";
import StatusPill from "../../app-components/ui/StatusPill";
import { btnPrimary, card, inputBase } from "../../app-components/ui/styles";
import TableSkeleton from "../../app-components/ui/TableSkeleton";
import { TransactionInterface } from "../../interface/transactions.interface";
import { formatDate, getErrorMessage } from "../../utils";
import { fmtMoney } from "../dashboard/components/format";
import { downloadCsvExport } from "../../utils/exportCsv";
import ExportButton from "../../app-components/ui/ExportButton";

/** Fields the API added (Phase 1b): what the gateway really charged, and why a payment failed */
type LedgerRow = TransactionInterface & {
  currency?: string | null;
  paid_amount?: string | number | null;
  gateway?: string | null;
  failure_reason?: string | null;
  verified_at?: string | null;
  subscription: TransactionInterface["subscription"] & {
    user?: TransactionInterface["subscription"]["user"] & { linkedin_full_name?: string | null };
  };
};

type ListParams = {
  page: number;
  per_page: number;
  status: string;
  currency: string;
  search: string;
  dateRange: string;
  subscriptionPlan: string;
};

const TABS = [
  { value: "", label: "All" },
  { value: "PAID", label: "Paid" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
];

const PLAN_OPTIONS = [
  { value: "STANDARD", label: "Standard" },
  { value: "BRONZE", label: "Bronze" },
  { value: "SILVER", label: "Silver" },
  { value: "GOLD", label: "Gold" },
  { value: "ELITEGOLD", label: "Elite Gold" },
];

const CURRENCY_OPTIONS = [
  { value: "NGN", label: "NGN (₦)" },
  { value: "USD", label: "USD ($)" },
];

const FAILURE_REASONS: Record<string, string> = {
  amount_mismatch: "Amount mismatch",
  gateway_failed: "Declined by gateway",
};

// Optional columns the admin can show or hide. Name is always shown.
const COLUMNS: ColumnDef[] = [
  { key: "date", label: "Date" },
  { key: "tranxId", label: "Tranx Id" },
  { key: "email", label: "Email", defaultVisible: false },
  { key: "amount", label: "Amount" },
  { key: "plan", label: "Plan" },
  { key: "duration", label: "Duration" },
  { key: "gateway", label: "Gateway" },
  { key: "method", label: "Method", defaultVisible: false },
  { key: "reference", label: "Payment Reference" },
  { key: "status", label: "Status" },
  { key: "start", label: "Starts", defaultVisible: false },
  { key: "end", label: "Ends", defaultVisible: false },
];

const th =
  "py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-hcMuted bg-hcHead whitespace-nowrap";
const td = "py-3 px-4 text-left text-sm whitespace-nowrap";

const PER_PAGE_KEY = "hc_per_page_transactions";
const readPerPage = () => {
  try {
    const n = Number(localStorage.getItem(PER_PAGE_KEY));
    return PER_PAGE_OPTIONS.includes(n) ? n : PER_PAGE_OPTIONS[0];
  } catch {
    return PER_PAGE_OPTIONS[0];
  }
};

const isFreeTrial = (t: LedgerRow) => (t.payment_reference ?? "").toUpperCase() === "FREE TRIAL";

/** The amount in the currency it was paid in; old rows without a currency show the bare number */
const amountText = (t: LedgerRow) => {
  if (isFreeTrial(t)) return "Free trial";
  const cur = (t.currency ?? "").toUpperCase();
  const value = Number(t.amount);
  if (cur === "NGN" || cur === "USD") return fmtMoney(cur, Number.isFinite(value) ? value : undefined);
  return Number.isFinite(value) ? value.toLocaleString() : "–";
};

const memberName = (t: LedgerRow) =>
  t.subscription?.user?.full_name || t.subscription?.user?.linkedin_full_name || "No name";

const gatewayLabel = (g?: string | null) =>
  !g ? "–" : g.toLowerCase().includes("flutter") ? "Flutterwave" : g.toLowerCase().includes("paystack") ? "Paystack" : g;

const Transactions = () => {
  const [pageData, setPageData] = useState<LedgerRow[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [perPage, setPerPage] = useState<number>(readPerPage);
  const [pagePerPage, setPagePerPage] = useState<number | undefined>(undefined);

  // status tab lives in the URL (?status=...) so sidebar submenus can link to it
  const [pageFilter, setPageFilter] = useStatusParam();
  useEffect(() => setCurrentPage(1), [pageFilter]);

  const [searchInput, setSearchInput] = useState("");
  const [searchInputDebounce, setSearchInputDebounce] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [subscriptionPlan, setSubscriptionPlan] = useState("");
  const [currency, setCurrency] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [dateError, setDateError] = useState("");
  const [loadError, setLoadError] = useState("");
  // only the latest request may update the table (fast filter changes)
  const requestSeq = useRef(0);

  const { isVisible, toggle, visibleCount } = useColumnVisibility("cols_transactions_v2", COLUMNS);
  const show = (key: string) => isVisible(key);

  const listParams = useCallback(
    (): ListParams => ({
      page: currentPage,
      per_page: perPage,
      status: pageFilter,
      currency,
      search: searchInput,
      dateRange,
      subscriptionPlan,
    }),
    [currentPage, perPage, pageFilter, currency, searchInput, dateRange, subscriptionPlan]
  );

  const getTransactions = async (params: ListParams) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setLoadError("");
    try {
      const res: any = await apiClient.get("transactions", { params });
      if (seq !== requestSeq.current) return; // a newer request replaced this one
      const body = res?.data ?? {};
      setPageData(Array.isArray(body.data) ? body.data : []);
      // only accept real page numbers (a bad response must not trigger a refetch loop)
      if (Number.isInteger(body.current_page) && body.current_page !== currentPage) setCurrentPage(body.current_page);
      setLastPage(Number(body.last_page) || 0);
      setTotal(typeof body.total === "number" ? body.total : undefined);
      const used = Number(body.per_page);
      setPagePerPage(Number.isFinite(used) && used > 0 ? used : undefined);
    } catch (error) {
      if (seq !== requestSeq.current) return;
      setLoadError(getErrorMessage(error));
      setPageData([]);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    getTransactions(listParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, pageFilter, currency, searchInput, dateRange, subscriptionPlan]);

  // debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchInput(searchInputDebounce);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInputDebounce]);

  useEffect(() => {
    setDateError("");
    if (startDate && endDate) {
      if (new Date(endDate) >= new Date(startDate)) {
        setDateRange(startDate + "," + endDate);
        setCurrentPage(1);
      } else {
        setDateError("End date must be on or after the start date.");
      }
    }
    if (!startDate && !endDate) setDateRange("");
  }, [startDate, endDate]);

  const setDates = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  const changePerPage = (n: number) => {
    try {
      localStorage.setItem(PER_PAGE_KEY, String(n));
    } catch {
      /* not remembered if storage is unavailable */
    }
    setPerPage(n);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchInputDebounce("");
    setSearchInput("");
    setSubscriptionPlan("");
    setCurrency("");
    setDates("", "");
    setCurrentPage(1);
  };

  const chips: FilterChip[] = [];
  if (searchInput)
    chips.push({
      label: `Search: ${searchInput}`,
      onRemove: () => {
        setSearchInputDebounce("");
        setSearchInput("");
        setCurrentPage(1);
      },
    });
  if (subscriptionPlan)
    chips.push({
      label: `Plan: ${PLAN_OPTIONS.find((p) => p.value === subscriptionPlan)?.label ?? subscriptionPlan}`,
      onRemove: () => {
        setSubscriptionPlan("");
        setCurrentPage(1);
      },
    });
  if (currency)
    chips.push({
      label: `Currency: ${currency}`,
      onRemove: () => {
        setCurrency("");
        setCurrentPage(1);
      },
    });
  if (dateRange)
    chips.push({ label: `Date: ${startDate} to ${endDate}`, onRemove: () => setDates("", "") });

  const hasRecords = pageData.length > 0;
  const rowsPerPage = pagePerPage ?? perPage;

  const tabLabel = TABS.find((t) => t.value === pageFilter)?.label;

  return (
    <AdminLayout title="Admin | Transactions">
      <PageHeader
        title="Transactions"
        subtitle="Every payment, as recorded. Each amount is shown in the currency it was paid in."
        actions={
          <ExportButton
            total={total}
            noun="transactions"
            disabled={loading}
            onExport={() => downloadCsvExport("transactions/export", { ...listParams() }, "transactions", total, "transactions")}
          />
        }
      />

      <div className={`${card} py-5`}>
        {/* tabs */}
        <div className="px-5 flex items-center gap-1 mb-5 border-b border-hcBorder">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => {
                setPageFilter(t.value);
                setCurrentPage(1);
              }}
              className={`px-4 pb-3 text-sm font-medium -mb-px border-b-2 transition ${
                pageFilter === t.value
                  ? "text-honeConnectPurple border-honeConnectPurple"
                  : "text-honeConnectGray border-transparent hover:text-honeConnectBlack"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* filter bar */}
        <div className="px-5 flex items-center gap-3 flex-wrap mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              value={searchInputDebounce}
              onChange={(e) => setSearchInputDebounce(e.target.value)}
              type="text"
              aria-label="Search transactions"
              className={`${inputBase} pl-9 w-full`}
              placeholder="Search by name, email, tranx id, reference or amount"
            />
          </div>

          <select
            aria-label="Currency"
            className={inputBase}
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All currencies</option>
            {CURRENCY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>

          <select
            aria-label="Plan"
            className={inputBase}
            value={subscriptionPlan}
            onChange={(e) => {
              setSubscriptionPlan(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All plans</option>
            {PLAN_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>

          <ColumnMenu columns={COLUMNS} isVisible={isVisible} onToggle={toggle} />
        </div>

        <div className="px-5 mb-4">
          <DateFilter startDate={startDate} endDate={endDate} onChange={setDates} />
          {dateError && (
            <p role="alert" className="mt-2 text-sm text-red-600">
              {dateError}
            </p>
          )}
        </div>

        <ActiveFilters chips={chips} onClearAll={clearFilters} />

        {loadError && !loading && !hasRecords ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center border-y border-hcBorder">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="font-semibold text-honeConnectBlack mb-1">Couldn't load transactions</p>
            <p className="text-sm text-hcMuted mb-4 max-w-md">{loadError}</p>
            <button onClick={() => getTransactions(listParams())} className={btnPrimary}>
              Try again
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto border-y border-hcBorder">
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  {show("date") && <th className={th}>Date</th>}
                  {show("tranxId") && <th className={th}>Tranx Id</th>}
                  <th className={`${th} left-0 z-20`} style={{ position: "sticky" }}>
                    Name
                  </th>
                  {show("email") && <th className={th}>Email</th>}
                  {show("amount") && <th className={`${th} text-right`}>Amount</th>}
                  {show("plan") && <th className={th}>Plan</th>}
                  {show("duration") && <th className={th}>Duration</th>}
                  {show("gateway") && <th className={th}>Gateway</th>}
                  {show("method") && <th className={th}>Method</th>}
                  {show("reference") && <th className={th}>Payment Reference</th>}
                  {show("status") && <th className={th}>Status</th>}
                  {show("start") && <th className={th}>Starts</th>}
                  {show("end") && <th className={th}>Ends</th>}
                </tr>
              </thead>

              {loading ? (
                <TableSkeleton columns={visibleCount + 1} />
              ) : (
                <tbody className="text-honeConnectBlack">
                  {pageData.map((t) => {
                    const months = Number(t.subscription?.subscription_plan_duration);
                    return (
                      <tr key={t.id} className="group border-b border-hcBorder hover:bg-hcBg">
                        {show("date") && (
                          <td className={`${td} text-hcMuted`}>
                            {t.created_at ? formatDate(t.created_at, "date") : "–"}
                          </td>
                        )}
                        {show("tranxId") && <td className={td}>{t.transaction_code ?? "–"}</td>}
                        <td
                          className={`${td} font-medium bg-white group-hover:bg-hcBg z-[1]`}
                          style={{ position: "sticky", left: 0 }}
                        >
                          {memberName(t)}
                        </td>
                        {show("email") && <td className={td}>{t.subscription?.user?.email ?? "–"}</td>}
                        {show("amount") && (
                          <td
                            className={`${td} text-right font-medium ${isFreeTrial(t) ? "text-hcMuted font-normal" : ""}`}
                            title={!isFreeTrial(t) && !t.currency ? "Currency not recorded for this payment" : undefined}
                          >
                            {amountText(t)}
                          </td>
                        )}
                        {show("plan") && (
                          <td className={`${td} capitalize`}>
                            {(t.subscription?.subscription_plan ?? "–").toLowerCase().replace("elitegold", "elite gold")}
                          </td>
                        )}
                        {show("duration") && (
                          <td className={td}>
                            {Number.isFinite(months) && months > 0 ? `${months} month${months === 1 ? "" : "s"}` : "–"}
                          </td>
                        )}
                        {show("gateway") && <td className={td}>{gatewayLabel(t.gateway)}</td>}
                        {show("method") && <td className={td}>{t.payment_method ?? "–"}</td>}
                        {show("reference") && <td className={`${td} font-mono text-xs`}>{t.payment_reference}</td>}
                        {show("status") && (
                          <td className={td}>
                            <div className="flex items-center gap-2">
                              {t.status && (
                                <StatusPill label={t.status} tone={t.status === "PENDING" ? "info" : undefined} />
                              )}
                              {t.failure_reason && (
                                <span className="text-xs text-red-600">
                                  {FAILURE_REASONS[t.failure_reason] ?? t.failure_reason}
                                </span>
                              )}
                            </div>
                          </td>
                        )}
                        {show("start") && (
                          <td className={td}>
                            {t.subscription?.start_date ? formatDate(t.subscription.start_date, "date") : "–"}
                          </td>
                        )}
                        {show("end") && (
                          <td className={td}>
                            {t.subscription?.end_date ? formatDate(t.subscription.end_date, "date") : "–"}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>

            {!loading && !hasRecords && (
              <EmptyState
                title={
                  chips.length
                    ? "No transactions match these filters"
                    : pageFilter
                    ? `No ${(tabLabel ?? pageFilter).toLowerCase()} transactions`
                    : "No transactions yet"
                }
                message={
                  chips.length
                    ? "Try changing your search or filters."
                    : pageFilter
                    ? "There are no transactions with this status right now."
                    : "Payments will appear here as members subscribe."
                }
                onClear={chips.length ? clearFilters : undefined}
              />
            )}
          </div>
        )}

        {hasRecords && (
          <Pagination
            currentPage={currentPage}
            lastPage={lastPage}
            total={total}
            onPageChange={setCurrentPage}
            perPage={perPage}
            pageSize={rowsPerPage}
            onPerPageChange={changePerPage}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default Transactions;
